import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminLogin from "@/pages/AdminLogin";
import AuthCallback from "@/pages/AuthCallback";
import StudentDashboard from "@/pages/StudentDashboard";
import TeacherDashboard from "@/pages/TeacherDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CourseLibrary from "@/pages/CourseLibrary";
import CourseDetail from "@/pages/CourseDetail";
import CourseViewer from "@/pages/CourseViewer";
import CourseStudents from "@/pages/CourseStudents";
import CreateCourse from "@/pages/CreateCourse";
import EditCourse from "@/pages/EditCourse";
import HomeworkSubmission from "@/pages/HomeworkSubmission";
import SchoolSettings from "@/pages/SchoolSettings";
import NotificationSettings from "@/pages/NotificationSettings";
import ModuleEnrollmentReport from "@/pages/ModuleEnrollmentReport";
import MyCertificates from "@/pages/MyCertificates";
import StudentCertificates from "@/pages/StudentCertificates";
import ModulesList from "@/pages/ModulesList";
import CreateModule from "@/pages/CreateModule";
import MyEnrolledCourses from "@/pages/MyEnrolledCourses";
import CourseActivities from "@/pages/CourseActivities";
import CourseDiscussions from "@/pages/CourseDiscussions";
import CourseQuizzes from "@/pages/CourseQuizzes";
import CreateQuiz from "@/pages/CreateQuiz";
import TakeQuiz from "@/pages/TakeQuiz";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        // Get token from localStorage - this is the PRIMARY auth method
        const storedToken = localStorage.getItem('auth_token');
        
        if (!storedToken) {
          throw new Error('No token found');
        }
        
        const response = await fetch(`${API}/auth/me`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${storedToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Not authenticated');
        }
        
        const text = await response.text();
        const userData = JSON.parse(text);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        // Clear stored token on auth failure
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    
    // Small delay to ensure localStorage is updated after login
    const timeoutId = setTimeout(checkAuth, 100);
    return () => clearTimeout(timeoutId);
  }, [navigate, location.state, location.pathname]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <div className="text-[#3A5A40] text-lg">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return children({ user });
};

const AppRouter = () => {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => (user?.role === 'teacher' || user?.role === 'admin') ? <TeacherDashboard user={user} /> : <StudentDashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses"
        element={
          <ProtectedRoute>
            {({ user }) => <CourseLibrary user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {({ user }) => <AdminDashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId"
        element={
          <ProtectedRoute>
            {({ user }) => <CourseDetail user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/view"
        element={
          <ProtectedRoute>
            {({ user }) => <CourseViewer user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/create"
        element={
          <ProtectedRoute>
            {({ user }) => <CreateCourse user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/edit"
        element={
          <ProtectedRoute>
            {({ user }) => <EditCourse user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/students"
        element={
          <ProtectedRoute>
            {({ user }) => <CourseStudents user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/homework/:courseId"
        element={
          <ProtectedRoute>
            {({ user }) => <HomeworkSubmission user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-courses"
        element={
          <ProtectedRoute>
            {({ user }) => <MyEnrolledCourses user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-courses/:courseId/activities"
        element={
          <ProtectedRoute>
            {({ user }) => <CourseActivities user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-courses/:courseId/discussions"
        element={
          <ProtectedRoute>
            {({ user }) => <CourseDiscussions user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/quizzes"
        element={
          <ProtectedRoute>
            {({ user }) => <CourseQuizzes user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/quizzes/create"
        element={
          <ProtectedRoute>
            {({ user }) => <CreateQuiz user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/quizzes/:quizId/take"
        element={
          <ProtectedRoute>
            {({ user }) => <TakeQuiz user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {({ user }) => <SchoolSettings user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            {({ user }) => <NotificationSettings user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/:moduleId/report"
        element={
          <ProtectedRoute>
            {({ user }) => <ModuleEnrollmentReport user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/certificates"
        element={
          <ProtectedRoute>
            {({ user }) => <MyCertificates user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/student-certificates"
        element={
          <ProtectedRoute>
            {({ user }) => <StudentCertificates user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules"
        element={
          <ProtectedRoute>
            {({ user }) => <ModulesList user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/modules/create"
        element={
          <ProtectedRoute>
            {({ user }) => <CreateModule user={user} />}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;